// src/features/equipment/components/maintenance/EquipmentMaintenanceTab.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
} from '@mui/material';
import {
  History as HistoryIcon,
  PostAdd as PostAddIcon,
  AttachFile as AttachFileIcon,
  Build as BuildIcon,
  ReportProblem as ReportProblemIcon,
} from '@mui/icons-material';

import MaintenanceStatusChip, { useFormatDateTime } from './MaintenanceStatusChip';
import { useEquipmentMaintenance } from '../../hooks/useEquipmentMaintenance';

const PAGE_SIZE = 100;

function OpenCaseCard({ maintenanceCase: c, onOpen }) {
  const { t } = useTranslation(['equipment']);
  const formatDateTime = useFormatDateTime();
  const pulled = c.status === 'in_progress';
  const Icon = pulled ? BuildIcon : ReportProblemIcon;

  return (
    <Paper
      variant="outlined"
      onClick={() => onOpen(c.maintenance_id)}
      sx={{
        p: 2,
        cursor: 'pointer',
        borderColor: pulled ? 'info.main' : 'warning.main',
        borderLeftWidth: 4,
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box display="flex" alignItems="center" gap={1.5}>
        <Icon color={pulled ? 'info' : 'warning'} />
        <Box flex={1} minWidth={0}>
          <Typography variant="subtitle2" noWrap>{c.title}</Typography>
          <Typography variant="caption" color="text.secondary">
            {pulled
              ? t('equipment:maintenance.pulled_by_at', { name: c.pulled_by?.name ?? '–', date: formatDateTime(c.pulled_at) })
              : t('equipment:maintenance.reported_by_at', { name: c.reported_by?.name ?? '–', date: formatDateTime(c.reported_at) })}
            {pulled && c.service_provider && ` · ${c.service_provider}`}
          </Typography>
        </Box>
        <MaintenanceStatusChip status={c.status} />
      </Box>
    </Paper>
  );
}

export default function EquipmentMaintenanceTab({ equipment, refreshKey, actions, onOpenCase }) {
  const { t } = useTranslation(['equipment', 'common']);
  const formatDateTime = useFormatDateTime();
  const { listCases } = useEquipmentMaintenance();
  const [cases, setCases] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    const result = await listCases(equipment.equipment_id, { limit: PAGE_SIZE });
    if (result.success) {
      setCases(result.data.items || []);
      setTotal(result.data.total || 0);
    } else {
      setError(result.error || t('equipment:maintenance.errors.load_failed'));
    }
    setLoading(false);
  }, [equipment.equipment_id, listCases, t]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const openCases = cases.filter(c => c.status === 'reported' || c.status === 'in_progress');
  const history = cases.filter(c => !openCases.includes(c));

  if (loading) {
    return <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>;
  }

  return (
    <Box p={3} display="flex" flexDirection="column" gap={3}>
      {error && <Alert severity="error">{error}</Alert>}

      {openCases.length > 0 && (
        <Box display="flex" flexDirection="column" gap={1.5}>
          <Typography variant="subtitle1" fontWeight={600}>{t('equipment:maintenance.open_cases')}</Typography>
          {openCases.map(c => <OpenCaseCard key={c.maintenance_id} maintenanceCase={c} onOpen={onOpenCase} />)}
        </Box>
      )}

      <Box>
        <Box display="flex" alignItems="center" gap={2} mb={1.5}>
          <Typography variant="subtitle1" fontWeight={600} flex={1}>
            {t('equipment:maintenance.history')}
          </Typography>
          {actions.canMaintain && equipment.status !== 'retired' && (
            <Button size="small" variant="outlined" startIcon={<PostAddIcon />} onClick={actions.openRecord}>
              {t('equipment:maintenance.actions.record')}
            </Button>
          )}
        </Box>

        {history.length === 0 ? (
          <Box display="flex" flexDirection="column" alignItems="center" py={5} color="text.secondary">
            <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
            <Typography variant="body2">{t('equipment:maintenance.no_history')}</Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('equipment:maintenance.columns.date')}</TableCell>
                  <TableCell>{t('equipment:maintenance.columns.title')}</TableCell>
                  <TableCell>{t('equipment:maintenance.columns.status')}</TableCell>
                  <TableCell>{t('equipment:maintenance.columns.service_provider')}</TableCell>
                  <TableCell>{t('equipment:maintenance.columns.outcome')}</TableCell>
                  <TableCell align="right"><AttachFileIcon fontSize="small" sx={{ verticalAlign: 'middle' }} /></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map(c => (
                  <TableRow key={c.maintenance_id} hover sx={{ cursor: 'pointer' }} onClick={() => onOpenCase(c.maintenance_id)}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(c.closed_at || c.created_at)}</TableCell>
                    <TableCell>{c.title}</TableCell>
                    <TableCell><MaintenanceStatusChip status={c.status} /></TableCell>
                    <TableCell>{c.service_provider || '–'}</TableCell>
                    <TableCell>{c.equipment_outcome ? t(`equipment:maintenance.outcomes.${c.equipment_outcome}`) : '–'}</TableCell>
                    <TableCell align="right">{c.file_count || ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {total > cases.length && (
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            {t('equipment:maintenance.showing_latest', { count: cases.length, total })}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
